import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { AlertTriangle } from 'lucide-react';

interface TypeToConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  confirmationText: string;
  confirmationLabel?: string;
  onConfirm: () => void;
  loading?: boolean;
  destructiveAction?: string;
}

export function TypeToConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmationText,
  confirmationLabel = 'Type the name to confirm',
  onConfirm,
  loading = false,
  destructiveAction = 'Delete',
}: TypeToConfirmDialogProps) {
  const [inputValue, setInputValue] = useState('');
  const isConfirmed = inputValue === confirmationText;

  // Reset input when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setInputValue('');
    }
  }, [open]);

  const handleConfirm = () => {
    if (isConfirmed && !loading) {
      onConfirm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <DialogTitle className="text-red-600">{title}</DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <Label htmlFor="confirmation" className="text-sm font-medium text-red-800">
              {confirmationLabel}
            </Label>
            <p className="mt-1 text-xs text-red-600">
              Please type <strong className="font-mono">{confirmationText}</strong> to confirm
            </p>
            <Input
              id="confirmation"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={confirmationText}
              className="mt-2 border-red-300 focus:border-red-500 focus:ring-red-500"
              autoComplete="off"
              disabled={loading}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!isConfirmed || loading}
          >
            {loading ? 'Processing...' : destructiveAction}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
